import React from 'react';
import LegalShell from './legal/LegalShell';
import Redacted from './legal/Redacted';

const H2 = ({ n, children }) => (
  <h2 className="text-xl font-extrabold text-white mt-8 mb-2">
    <span style={{ color: '#00D26A' }}>{n}.</span> {children}
  </h2>
);
const H3 = ({ n, children }) => (
  <h3 className="text-base font-bold text-white mt-5 mb-1">
    <span className="text-zinc-500">{n}</span> {children}
  </h3>
);

export default function TermsOfService() {
  return (
    <LegalShell title="Terms of Service" lastUpdated="20 April 2026" version="v1 DRAFT">
      <p>
        Welcome to Flipping Friends. By creating an account, visiting our website, or using any
        of our services, you agree to these Terms of Service (the <strong>&ldquo;Terms&rdquo;</strong>).
        Please read them carefully. If you do not agree, you must not use the service.
      </p>

      <H2 n={1}>Who we are</H2>
      <p>
        &ldquo;<strong>Flipping Friends</strong>&rdquo;, &ldquo;<strong>we</strong>&rdquo;,
        &ldquo;<strong>us</strong>&rdquo; and &ldquo;<strong>our</strong>&rdquo; means Flipping Friends
        Ltd, a company being incorporated in England and Wales. The Companies House registration
        number will be published on this page once issued. Our registered contact address is
        available via{' '}
        <a href="mailto:marcus@flippingfriends.co.uk" className="text-[#00D26A] underline">
          marcus@flippingfriends.co.uk
        </a>
        .
      </p>

      <H2 n={2}>What Flipping Friends does</H2>
      <p>
        Flipping Friends is an <strong>informational software tool</strong> for UK resellers of
        media goods (books, DVDs, Blu-rays, CDs, video games). It compares prices that
        third-party buyers (<Redacted count={4} hint="four major UK recommerce buyers" />) are
        advertising for specific barcodes, helps users catalogue their inventory, and connects
        users who want a specific item with users who have scanned one.
      </p>
      <p className="font-semibold text-white">Flipping Friends is NOT:</p>
      <ul className="list-disc pl-6 space-y-1 text-zinc-300">
        <li>a buyer or seller of the items you scan</li>
        <li>a payment processor or escrow service</li>
        <li>a shipping, fulfilment or returns handler</li>
        <li>a representative, agent or partner of <Redacted count={4} hint="four UK recommerce buyers" />, or of eBay, Vinted or any other marketplace</li>
      </ul>
      <p>
        All transactions that follow from using Flipping Friends happen directly between you
        and the relevant third-party platform (e.g. eBay, Vinted, or a recycling buyer). We do
        not handle money, post, feedback or dispute resolution for those transactions.
      </p>

      <H2 n={3}>Eligibility</H2>
      <p>
        You must be <strong>18 years of age or older</strong> and legally able to enter into
        contracts under the laws of England and Wales. Accounts are personal to you and you
        must not share your login with anyone else.
      </p>

      <H2 n={4}>Your account</H2>
      <p>
        Accounts are created via Google Sign-In. You may be placed on an email-allowlist beta
        programme; inclusion is at our discretion and may be withdrawn at any time.
      </p>
      <p>
        You are responsible for keeping your account secure, for all activity taking place
        under your account, and for ensuring the contact email on file is current.
      </p>

      <H2 n={5}>Earnings, credits and payouts</H2>
      <H3 n="5.1">How earnings are calculated</H3>
      <p>
        When you submit a verified price for a barcode, we credit your Flipping Friends balance
        at the published rate (currently <strong>£0.01 per verified price point</strong>, up to 5
        price points per fully-checked barcode). Rates may change; the live rate is displayed in
        your Dashboard.
      </p>
      <H3 n="5.2">Verification</H3>
      <p>
        A price is considered &ldquo;verified&rdquo; when it has been independently submitted by
        a second Flipping Friends user, or when it has been automatically verified by an admin
        account. Submissions found to be fraudulent, duplicated, or materially inaccurate will
        be rejected and associated earnings reversed.
      </p>
      <H3 n="5.3">Batch Price Checking &ldquo;sacrifice&rdquo; protocol</H3>
      <p>
        To maintain the integrity of our price database, batch price checks require users to
        submit a small list of <strong>known-calibration (&ldquo;sacrifice&rdquo;) barcodes</strong>{' '}
        first, confirm the expected per-company totals, and only then submit the remainder of
        the batch. Submissions that skip or tamper with the sacrifice step will be rejected,
        the batch forfeit, and the account may be suspended.
      </p>
      <H3 n="5.4">Payouts</H3>
      <p>
        Payouts will be made to a UK bank account, subject to a minimum withdrawal threshold
        published in your Dashboard and successful completion of identity verification with our
        payment processor. Processing times and fees are set by that processor. Payment rails
        are not live at the date of this draft; this section will be updated before payouts
        begin.
      </p>

      <H2 n={6}>Community Match &amp; third-party marketplace disclaimer</H2>
      <p>
        Our <strong>Community Match</strong> feature notifies users when someone has scanned a
        barcode that another user has added to their &ldquo;wanted list&rdquo;. Any resulting
        transaction takes place on a third-party marketplace (eBay, Vinted, or similar),
        governed by that marketplace&apos;s own terms, buyer protection and dispute policies.
      </p>
      <p>
        Flipping Friends is not a party to those transactions. We do not guarantee the
        authenticity, condition, availability, shipping, payment or fitness-for-purpose of any
        item. We do not handle, hold, transfer or refund funds. All communication between users
        must be conducted through the marketplace in question — sharing personal phone numbers,
        email addresses or payment details through Flipping Friends messaging is prohibited
        (see Community Guidelines).
      </p>

      <H2 n={7}>Acceptable use</H2>
      <p>You must not use Flipping Friends to:</p>
      <ul className="list-disc pl-6 space-y-1 text-zinc-300">
        <li>submit fraudulent, automated, bulk-scripted or knowingly inaccurate price data</li>
        <li>scrape, reverse-engineer, resell or re-host our data in bulk without written consent</li>
        <li>harass, threaten, stalk or otherwise harm other users</li>
        <li>misrepresent items, inflate prices, or engage in collusion or price-gouging</li>
        <li>infringe any intellectual property rights, publicity rights or privacy rights</li>
        <li>transmit malware, spam, or content that is unlawful, offensive, obscene or defamatory</li>
        <li>circumvent rate limits, security controls or account bans</li>
      </ul>
      <p>We may suspend or terminate any account that breaches this clause without prior notice.</p>

      <H2 n={8}>Intellectual property</H2>
      <p>
        The Flipping Friends service, including the software, brand, database of aggregated
        prices, and website copy, is owned by Flipping Friends Ltd and protected by UK and
        international copyright, trademark and database-right laws.
      </p>
      <p>
        <strong>Your licence to us:</strong> when you submit barcode scans, prices, donations,
        wanted-list entries or match data, you grant Flipping Friends a worldwide, royalty-free,
        non-exclusive, perpetual licence to store, aggregate, anonymise and use that data to
        operate and improve the service. You keep ownership of your own content.
      </p>

      <H2 n={9}>Service availability &amp; changes</H2>
      <p>
        We aim for high availability but make no uptime guarantees. The service is provided
        &ldquo;as is&rdquo; and &ldquo;as available&rdquo;. We may change, suspend or discontinue
        parts of the service at any time, and will give reasonable notice of material changes
        affecting paying users.
      </p>

      <H2 n={10}>Fees &amp; subscriptions</H2>
      <p>
        Future paid tiers (Home Collection, Part-Time, Full-Time, Elite) will be priced as
        published on our website. Pricing can change on renewal with at least 14 days&apos;
        notice. Subscriptions auto-renew unless cancelled before the renewal date. UK consumer
        law cooling-off rights apply to new subscriptions started via the website.
      </p>

      <H2 n={11}>Liability</H2>
      <p>
        Nothing in these Terms limits our liability for death or personal injury caused by our
        negligence, for fraud, or any other liability that cannot be excluded under English law.
      </p>
      <p>
        Subject to the above, to the maximum extent permitted by law, Flipping Friends is not
        liable for: (a) any indirect, incidental, special or consequential loss; (b) loss of
        profit, revenue, data or goodwill; (c) losses caused by third-party platforms
        (including <Redacted count={4} hint="four UK recommerce buyers" />, and any resale marketplace such as eBay or Vinted); (d)
        price-data being out of date or inaccurate; (e) decisions you make based on our
        output. Our aggregate liability in any 12-month period shall not exceed the greater of
        (i) £100 or (ii) the total fees paid by you to Flipping Friends in that period.
      </p>

      <H2 n={12}>Termination</H2>
      <p>
        You may delete your account at any time by emailing{' '}
        <a href="mailto:marcus@flippingfriends.co.uk" className="text-[#00D26A] underline">
          marcus@flippingfriends.co.uk
        </a>
        . We may terminate or suspend accounts that breach these Terms or our Community
        Guidelines. Clauses that by their nature should survive termination (earnings
        reversals, intellectual property, liability, governing law) will survive.
      </p>

      <H2 n={13}>Changes to these Terms</H2>
      <p>
        We may amend these Terms. Material changes will be notified by email to account holders
        and by a banner on the website at least 14 days before they take effect. Continued use
        of the service after the effective date is acceptance of the revised Terms.
      </p>

      <H2 n={14}>Governing law &amp; jurisdiction</H2>
      <p>
        These Terms are governed by the laws of <strong>England and Wales</strong>. The courts
        of England and Wales have exclusive jurisdiction over any dispute arising out of or in
        connection with these Terms, except that consumers resident in Scotland or Northern
        Ireland may also bring proceedings in the courts of their home jurisdiction.
      </p>

      <H2 n={15}>Contact</H2>
      <p>
        Questions? Email{' '}
        <a href="mailto:marcus@flippingfriends.co.uk" className="text-[#00D26A] underline">
          marcus@flippingfriends.co.uk
        </a>
        .
      </p>
    </LegalShell>
  );
}
